/**
 * My time's billing view: the viewer's own posted notes in one pay period,
 * across all her clients, newest first (GET /v1/my-prebilling,
 * view_prebilling). Each row is the Pre-billing row a care manager reads,
 * plus the client it is for; never a rate or an amount, for any seat.
 */

import { keepPreviousData, useQuery, type QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';
import { toNote, type PrebillingNote, type PrebillingRowWire } from './prebilling';
import type { HoursPeriodWire } from './timeEntriesApi';

export interface MyPrebillingRowWire extends PrebillingRowWire {
  client_id: string;
  client_display_name: string;
  client_is_demo: boolean;
  /** Whether the viewer's client scope reaches this client today. */
  client_in_scope: boolean;
}

interface MyPrebillingWire {
  period: HoursPeriodWire;
  rows: MyPrebillingRowWire[];
}

/** One of the viewer's notes, as the shared Pre-billing row reads it, and
 *  its client. */
export interface MyPrebillingNote {
  note: PrebillingNote;
  clientId: string;
  clientName: string;
  clientIsDemo: boolean;
  clientInScope: boolean;
}

export interface MyPrebillingFetch {
  period: HoursPeriodWire;
  rows: MyPrebillingNote[];
}

export function myPrebillingKey(slug: string, periodStart: string | null): readonly unknown[] {
  return ['my-prebilling', slug, periodStart] as const;
}

/** Refresh every period's billing view for this tenant. */
export function invalidateMyPrebilling(queryClient: QueryClient, slug: string): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: ['my-prebilling', slug] });
}

export async function fetchMyPrebilling(
  slug: string,
  periodStart: string | null,
  signal?: AbortSignal,
): Promise<MyPrebillingFetch> {
  const query = periodStart === null ? '' : `?period_start=${encodeURIComponent(periodStart)}`;
  const path = `/api/t/${encodeURIComponent(slug)}/v1/my-prebilling${query}`;
  const res = await fetch(path, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, body, `GET ${path} → HTTP ${res.status}`);
  }
  const payload = (await res.json()) as MyPrebillingWire;
  return {
    period: payload.period,
    rows: payload.rows.map((r) => ({
      // No native_from rides on this read: My time shows no export marker.
      note: toNote(r, null),
      clientId: r.client_id,
      clientName: r.client_display_name,
      clientIsDemo: r.client_is_demo === true,
      clientInScope: r.client_in_scope === true,
    })),
  };
}

export interface UseMyPrebillingResult {
  data: MyPrebillingFetch | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

export function useMyPrebilling(
  slug: string,
  periodStart: string | null,
  opts: { enabled?: boolean } = {},
): UseMyPrebillingResult {
  const query = useQuery<MyPrebillingFetch>({
    queryKey: myPrebillingKey(slug, periodStart),
    queryFn: ({ signal }) => fetchMyPrebilling(slug, periodStart, signal),
    staleTime: 30_000,
    // The previous period stays on screen while the next loads, as My
    // time's hours do: a jump that lands in the same period keeps the list
    // and its page, and the page never flashes empty between periods.
    placeholderData: keepPreviousData,
    enabled: (opts.enabled ?? true) && slug !== '',
  });
  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}
